import { Dropzone } from "@/components";
import useSnackbar from "@/hooks/useSnackbar.ts";
import useUploadFile from "@/hooks/useUploadFile.ts";
import { tokens } from "@/tokens";
import {
  Close as CloseIcon,
  InsertDriveFileOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { FC, ReactElement, useCallback, useState } from "react";
import { formatFileSize } from "@/utils/helpers.ts";

interface DataModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  subTitle: string;
  buttonAction: (ele: DropzoneTypes[]) => void;
  buttonText: string;
  btnLoader: boolean;
  setTimeStamp: React.Dispatch<React.SetStateAction<string>>;
}

const UploadData: FC<DataModalProps> = ({
  setShow,
  show,
  title,
  subTitle,
  buttonText,
  buttonAction,
  btnLoader = false,
  setTimeStamp,
}): ReactElement => {
  const { show: toast } = useSnackbar();
  const [uploads, setUploads] = useState<DropzoneTypes[]>([]);
  const { uploadFileTradeData } = useUploadFile();
  const [hasFile, setHasFile] = useState(false);

  const confirmAddDocument = () => {
    if (uploads.length === 0) return toast("Please Add File", "", "error");
    buttonAction(uploads);
  };

  const uploadDocument = useCallback(
    async (file: File) => {
      if (uploads.length > 0) return toast("Only one file allowed", "", "error");
      setHasFile(true);
      setUploads([
        {
          name: file?.name,
          size: file?.size,
          progress: 0,
          fileUrl: "",
        },
      ]);

      // Generate timestamp at upload time
      const uploadTimeStamp = new Date().toISOString();

      const data = await uploadFileTradeData(
        file,
        "1",
        uploadTimeStamp
      );
      const { key, timeStamp: actualTimeStamp } = data ?? {};

      if (!key) return;

      // Update the parent component's timestamp with the actual one used
      const finalTimeStamp = actualTimeStamp || uploadTimeStamp;
      setTimeStamp(finalTimeStamp);

      setUploads([{
        name: file?.name,
        size: file?.size,
        progress: 100,
        fileUrl: key,
      }]);
      setHasFile(false);
      toast("File successfully uploaded", "", "success");
    },
    [uploadFileTradeData, uploads.length, setTimeStamp]
  );

  const handleDeleteFile = () => {
    setUploads([]);
  };

  return (
    <Dialog
      open={show}
      onClose={() => setShow(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderBottom: `1px solid ${tokens.border.default}`,
      }}>
        <Box>
          <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: "15px", color: tokens.text.primary }}>
            {title}
          </Typography>
          {subTitle && (
            <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted, mt: 0.25 }}>
              {subTitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={() => setShow(false)} size="small" sx={{ color: tokens.text.secondary }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary, mb: 1.5 }}>
          Upload Trade Data File
        </Typography>
        <Dropzone
          onUpload={uploadDocument}
          extensions={["xlsb", "xlsx", "csv"]}
          fileFormat={"xlsb, xlsx, or csv"}
        />

        {/* File Upload Progress */}
        {uploads.map((ele: DropzoneTypes, index: number) => (
          <Box
            key={index}
            sx={{
              mt: 2,
              p: 2,
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.default}`,
              bgcolor: tokens.bg.hover,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Box sx={{
                  width: 36,
                  height: 36,
                  borderRadius: tokens.radius.md,
                  bgcolor: `${tokens.brand.primary}14`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <InsertDriveFileOutlined sx={{ fontSize: 20, color: tokens.brand.primary }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
                    {ele.name}
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                    {formatFileSize(ele.size)}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={() => handleDeleteFile()}
                size="small"
                sx={{ color: tokens.text.muted, "&:hover": { color: tokens.status.danger } }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>

            <Stack direction="row" alignItems="center" gap={1.5}>
              <LinearProgress
                variant="determinate"
                value={ele.progress}
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: tokens.border.default,
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    bgcolor: tokens.brand.primary,
                  },
                }}
              />
              <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, fontWeight: tokens.fontWeight.semibold, minWidth: 32 }}>
                {ele.progress}%
              </Typography>
            </Stack>
          </Box>
        ))}
      </DialogContent>

      {/* Footer */}
      <Box sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1.5,
        px: 3,
        py: 2,
        borderTop: `1px solid ${tokens.border.default}`,
      }}>
        <Button
          onClick={() => setShow(false)}
          variant="outlined"
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.body,
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            height: 34,
            px: 2.5,
            "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={confirmAddDocument}
          disabled={btnLoader || hasFile || uploads.length === 0}
          variant="contained"
          startIcon={btnLoader ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
          sx={{
            bgcolor: tokens.brand.primary,
            color: "#fff",
            borderRadius: tokens.radius.md,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            height: 34,
            px: 2.5,
            boxShadow: "none",
            "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: tokens.text.disabled, color: "#fff" },
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </Dialog>
  );
};

export default UploadData;
